/**
 * Breadcrumb configuration
 * Maps route patterns to breadcrumb labels and parent routes
 */

export interface BreadcrumbConfig {
  label: string;
  parent?: string;
  icon?: string;      // lucide icon name for regular pages
  prefix?: string;    // prefix for detail pages (e.g., "Site")
}

export const breadcrumbConfig: Record<string, BreadcrumbConfig> = {
  '/templates': { label: 'Templates', icon: 'LayoutPanelLeft' },
  '/sites': { label: 'Sites', icon: 'LayoutGrid' },
  '/sites/new': { label: 'Create site', icon: 'Plus', parent: '/sites' },
  '/sites/:siteId': { label: 'Site details', parent: '/sites', prefix: 'Site' },
  // Overview intentionally omitted — peels to /sites/:siteId so the chain ends at the site name.
  '/sites/:siteId/activity': { label: 'Activity log', parent: '/sites/:siteId' },
  '/sites/:siteId/analytics': { label: 'Analytics', parent: '/sites/:siteId' },
  '/sites/:siteId/edit': { label: 'Edit site', parent: '/sites/:siteId' },
  '/domains': { label: 'Domains', icon: 'Globe' },
  '/users': { label: 'Users', icon: 'Users' },
  '/users/:userId': { label: 'User details', parent: '/users', prefix: 'User' },
  '/profile': { label: 'Profile', icon: 'User' },
  '/settings': { label: 'Settings', icon: 'Settings' },
  '/billing': { label: 'Billing', icon: 'CreditCard' },
  '/pricing': { label: 'Pricing', icon: 'Tag' },
};

/**
 * Match a pathname against route patterns. If no exact match, peels trailing
 * segments until something matches — so leaf routes that aren't in the config
 * (e.g. tab URLs like /sites/:siteId/overview) inherit their nearest configured
 * parent's breadcrumb chain instead of disappearing.
 */
export function matchRoute(pathname: string): { pattern: string; params: Record<string, string> } | null {
  let candidate = pathname;
  while (candidate) {
    for (const pattern of Object.keys(breadcrumbConfig)) {
      const params = matchPattern(pattern, candidate);
      if (params !== null) {
        return { pattern, params };
      }
    }
    const lastSlash = candidate.lastIndexOf('/');
    if (lastSlash <= 0) return null;
    candidate = candidate.slice(0, lastSlash);
  }
  return null;
}

/**
 * Match a single pattern against a pathname
 * Returns extracted params or null if no match
 */
function matchPattern(pattern: string, pathname: string): Record<string, string> | null {
  const patternParts = pattern.split('/').filter(Boolean);
  const pathParts = pathname.split('/').filter(Boolean);

  if (patternParts.length !== pathParts.length) {
    return null;
  }

  const params: Record<string, string> = {};

  for (let i = 0; i < patternParts.length; i++) {
    const patternPart = patternParts[i];
    const pathPart = pathParts[i];

    if (patternPart.startsWith(':')) {
      // Dynamic segment - extract param
      const paramName = patternPart.slice(1);
      params[paramName] = pathPart;
    } else if (patternPart !== pathPart) {
      // Static segment mismatch
      return null;
    }
  }

  return params;
}
